import { Button, Card, Pagination, PermissionGate, Table, TableColumn } from '@/components/common';
import { PaginationMetadata, UserDTO } from '@/models';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface UserTableProps {
  users: UserDTO[];
  pagination: PaginationMetadata | null;
  searchTerm: string;
  currentPage: number;
  pageSize: number;
  pageSizeOptions: readonly number[];
  isLoading: boolean;
  onSearchChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onEdit: (user: UserDTO) => void;
  onDelete: (user: UserDTO) => void;
  onChangePassword: (user: UserDTO) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  pagination,
  searchTerm,
  currentPage,
  pageSize,
  pageSizeOptions,
  isLoading,
  onSearchChange,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  onChangePassword,
}) => {
  const navigate = useNavigate();

  const columns: TableColumn<UserDTO>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (user) => `${user.firstName || ''} ${user.lastName || ''}`.trim() || '-',
    },
    {
      key: 'email',
      header: 'Email',
      render: (user) => user.email || '-',
    },
    {
      key: 'institution',
      header: 'Institution',
      render: (user) => user.institution?.name || '-',
    },
    {
      key: 'rfid',
      header: 'RFID',
      render: (user) => {
        const rfidTags = (user.cards || [])
          .map((card) => card.rfidTag?.trim())
          .filter((rfidTag): rfidTag is string => Boolean(rfidTag));

        return rfidTags.length > 0 ? rfidTags.join(', ') : '-';
      },
    },
    {
      key: 'certified',
      header: 'Certified',
      render: (user) =>
        user.isCertified ? (
          <span className="badge bg-success">
            <i className="fas fa-check-circle me-1"></i>
            Certified
          </span>
        ) : (
          <span className="badge bg-secondary">Not Certified</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '260px',
      render: (user) => (
        <div className="d-flex gap-2 flex-wrap">
          <Button variant="primary" size="sm" icon="fas fa-edit" onClick={() => onEdit(user)}>
            Edit
          </Button>
          <PermissionGate action="user:edit">
            <Button
              variant="secondary"
              size="sm"
              icon="fas fa-lock"
              onClick={() => onChangePassword(user)}
            >
              Password
            </Button>
          </PermissionGate>
          <PermissionGate action="scope:edit">
            <Button
              variant="info"
              size="sm"
              icon="fas fa-key"
              onClick={() => navigate(`/users/${user.institutionId}/${user.id}/scopes`)}
            >
              Scopes
            </Button>
          </PermissionGate>
          <PermissionGate action="scope:edit">
            <Button
              variant="warning"
              size="sm"
              icon="fas fa-shield-alt"
              onClick={() => navigate(`/users/${user.institutionId}/${user.id}/permissions`)}
            >
              Permissions
            </Button>
            <Button variant="danger" size="sm" icon="fas fa-trash" onClick={() => onDelete(user)} />
          </PermissionGate>
        </div>
      ),
    },
  ];

  return (
    <Card className="border-0 shadow-sm">
      <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
        <div>
          <h5 className="mb-1">Users</h5>
          <p className="text-muted small mb-0">Search by name, email, institution, or RFID tag.</p>
        </div>
        <div style={{ maxWidth: '360px', width: '100%' }}>
          <div className="input-group">
            <span className="input-group-text">
              <i className="fas fa-search"></i>
            </span>
            <input
              type="search"
              className="form-control"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>
      </div>
      <Table
        columns={columns}
        data={users}
        keyExtractor={(user) => user.id}
        isLoading={isLoading}
        emptyMessage="No users found. Click 'Add User' to create one."
      />
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="d-flex align-items-center gap-2">
          <small className="text-muted">Rows per page:</small>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          {pagination && pagination.totalCount > 0 && (
            <small className="text-muted">
              Showing {(currentPage - 1) * pageSize + 1}–
              {Math.min(currentPage * pageSize, pagination.totalCount)} of {pagination.totalCount}{' '}
              users
            </small>
          )}
        </div>
        <Pagination pagination={pagination} onPageChange={onPageChange} />
      </div>
    </Card>
  );
};
